import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Send, Loader2, ChevronDown, Search, Trash2, Info,
  BookOpen, Sparkles, FileText, GitCompare, Wrench, ScanSearch,
  PanelRight, PanelRightClose,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useAuth } from "@/contexts/AuthContext";
import { useModel } from "@/contexts/ModelContext";
import { runJob } from "@/lib/jobs";
import WikifiMascot from "@/components/WikifiMascot";
import MyStackRail from "@/components/MyStackRail";
import OrchestratorPanel from "@/components/OrchestratorPanel";
import { PageHero } from "@/components/PageHero";
import {
  listModels, listConversations, getConversation, createConversation,
  appendUserMessage, deleteConversation,
} from "@/lib/wikifi";

/**
 * Wikifi Workspace — the primary "use AI" surface.
 *
 * Layout (desktop):
 *   ┌─────────────┬────────────────────────────────────────┐
 *   │  History    │  Conversation area                     │
 *   │  sidebar    │  (empty state → suggested prompts,     │
 *   │             │   with messages → scrolling thread)    │
 *   │             │                                        │
 *   │             │  Input bar (bottom)                    │
 *   └─────────────┴────────────────────────────────────────┘
 *
 * Everything routes through the existing async job pattern so we inherit its
 * Cloudflare-safe timeout handling. Model selection is state on this page —
 * "auto" (Wikifi orchestration) is the default; the picker offers the 3
 * supported frontier models as a manual override.
 */

const SUGGESTED_PROMPTS = [
  { icon: BookOpen,   label: "Research a company",             prompt: "Research " },
  { icon: FileText,   label: "Analyze this document",          prompt: "Analyze this document: " },
  { icon: GitCompare, label: "Compare these AI tools",         prompt: "Compare " },
  { icon: Sparkles,   label: "Help me create a strategy",      prompt: "Help me create a strategy for " },
  { icon: ScanSearch, label: "Find the best AI for this task", prompt: "Find the best AI tool for " },
];

function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const s = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function groupByDate(items) {
  const day = 86400_000;
  const now = Date.now();
  const buckets = { today: [], yesterday: [], week: [], older: [] };
  items.forEach((c) => {
    const d = new Date(c.updated_at).getTime();
    const diff = now - d;
    if (diff < day) buckets.today.push(c);
    else if (diff < 2 * day) buckets.yesterday.push(c);
    else if (diff < 7 * day) buckets.week.push(c);
    else buckets.older.push(c);
  });
  return buckets;
}

// ── Small subcomponents ────────────────────────────────────────────────────

function PoweredByWikifi({ meta }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2 -mb-1" data-testid="powered-by-wikifi">
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 text-[10.5px] text-[#8A93A6] hover:text-[#0B1220] transition-colors"
        aria-expanded={open}
      >
        Powered by Wikifi
        <ChevronDown size={10} className={`transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>
      {open && (
        <div className="mt-1.5 text-[11px] text-[#5B6478] bg-white border border-[#E7EAF0] rounded-md px-2.5 py-2 space-y-0.5 max-w-md">
          <div><span className="text-[#8A93A6]">Model:</span> {meta.model_used || "—"}</div>
          <div><span className="text-[#8A93A6]">Orchestration:</span> {meta.orchestration || "—"}</div>
          {typeof meta.models_consulted === "number" && (
            <div><span className="text-[#8A93A6]">Models consulted:</span> {meta.models_consulted}</div>
          )}
          <div className="text-[10px] text-[#8A93A6] pt-1">
            Tools used, sources & verification will appear here once available.
          </div>
        </div>
      )}
    </div>
  );
}

function HistoryItem({ conv, active, onOpen, onDelete }) {
  return (
    <div
      onClick={onOpen}
      className={`group cursor-pointer h-8 px-2 rounded-md flex items-center gap-1.5 transition-colors ${
        active ? "bg-[#0B1220] text-white" : "hover:bg-white text-[#0B1220]"
      }`}
      data-testid={`conv-item-${conv.conversation_id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-normal truncate leading-none">{conv.title || "Untitled"}</div>
      </div>
      <div className={`text-[11px] shrink-0 ${active ? "text-white/60" : "text-[#9CA3AF]"}`}>
        {relativeTime(conv.updated_at)}
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        className={`opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded ${
          active ? "hover:bg-white/10 text-white" : "hover:bg-[#E5E7EB] text-[#9CA3AF]"
        }`}
        aria-label="Delete conversation"
        data-testid={`conv-delete-${conv.conversation_id}`}
      >
        <Trash2 size={11} />
      </button>
    </div>
  );
}

function ModelPicker({ value, onChange, models }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  const current = value === "auto"
    ? { id: "auto", label: "Wikifi Auto" }
    : models.find((m) => m.id === value) || { id: "auto", label: "Wikifi Auto" };
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="h-8 px-3 rounded-md bg-white border border-[#E7EAF0] hover:border-[#0B1220]/40 text-[12px] font-semibold text-[#0B1220] inline-flex items-center gap-1.5 transition-colors"
        data-testid="model-picker-btn"
      >
        {value === "auto" && <span className="w-1.5 h-1.5 rounded-full bg-[#0B1220]" aria-hidden="true"/>}
        {current.label}
        <ChevronDown size={12}/>
      </button>
      {open && (
        <div className="absolute right-0 mt-1.5 w-72 bg-white border border-[#E7EAF0] rounded-md shadow-lg overflow-hidden z-30" data-testid="model-picker-menu">
          <button
            onClick={() => { onChange("auto"); setOpen(false); }}
            className={`w-full text-left px-3 py-2.5 hover:bg-[#F6F7FB] border-b border-[#E7EAF0] ${value === "auto" ? "bg-[#F6F7FB]" : ""}`}
            data-testid="model-option-auto"
          >
            <div className="flex items-center gap-2 mb-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#0B1220]"/>
              <span className="text-[12.5px] font-semibold text-[#0B1220]">Wikifi Auto</span>
              <span className="ml-auto text-[9px] font-bold uppercase tracking-wider text-white bg-[#0B1220] px-1.5 py-0.5 rounded">Recommended</span>
            </div>
            <div className="text-[11px] text-[#5B6478] leading-relaxed">
              Wikifi automatically selects the appropriate AI for your task.
            </div>
          </button>
          <div className="px-3 pt-2 pb-1 text-[9.5px] font-semibold uppercase tracking-wider text-[#8A93A6]">Choose a model</div>
          {models.map((m) => {
            const available = m.available !== false;
            return (
              <button
                key={m.id}
                onClick={() => { if (available) { onChange(m.id); setOpen(false); } }}
                disabled={!available}
                className={`w-full text-left px-3 py-2 text-[12px] flex items-center justify-between gap-2 ${
                  available
                    ? `hover:bg-[#F6F7FB] text-[#0B1220] cursor-pointer ${value === m.id ? "bg-[#F6F7FB] font-semibold" : ""}`
                    : "text-[#8A93A6] cursor-not-allowed"
                }`}
                data-testid={`model-option-${m.id}`}
              >
                <span className="truncate">{m.label}</span>
                {!available && (
                  <span className="text-[8.5px] font-bold uppercase tracking-wider text-[#8A93A6] bg-[#F6F7FB] border border-[#E7EAF0] px-1.5 py-0.5 rounded shrink-0" data-testid={`model-coming-soon-${m.id}`}>
                    Coming soon
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function WikifiWorkspace() {
  const { user } = useAuth();
  const { model: pickedModel, setModelId: setGlobalModelId, models: frontierModels } = useModel();
  const navigate = useNavigate();
  const [sp] = useSearchParams();

  const [models, setModels] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  // Initialise workspace mode from the globally-selected frontier model so
  // header + hero + workspace stay in sync. `wikifiId` maps our lib id
  // (gpt / claude / …) to whatever Wikifi's models call themselves.
  const [mode, setMode] = useState(() => pickedModel.wikifiId || "auto");
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");
  const [initialised, setInitialised] = useState(false);
  // History drawer state — default collapsed for signed-out users (they have
  // nothing to see), auto-opens once we know we have a signed-in user with
  // conversations. Toggleable via the top-bar panel icon.
  const [historyOpen, setHistoryOpen] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Bootstrap — load models + conversation list. Public models call has no
  // auth requirement so unauth users still see the workspace shell.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const m = await listModels();
        if (!cancelled) setModels(m.models || []);
      } catch (_) { /* non-fatal */ }
      if (!user) { setInitialised(true); return; }
      try {
        const items = await listConversations();
        if (!cancelled) {
          setConversations(items);
          // Auto-open the drawer for signed-in users with existing conversations
          // so history is visible without an extra click.
          if (items && items.length > 0) setHistoryOpen(true);
        }
      } catch (_) { /* non-fatal */ }
      setInitialised(true);
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Deep-link support: /wikifi?c=<conversation_id>
  useEffect(() => {
    const cid = sp.get("c");
    if (cid && cid !== activeId) setActiveId(cid);
  }, [sp, activeId]);

  // Deep-link support: /wikifi?q=<prompt> — sent from the global search bar.
  // Pre-fills the input and auto-sends once (for signed-in users) so the
  // Wikifi workspace immediately acts on the query the user just typed
  // upstream. The `q` param is stripped after handling so a refresh doesn't
  // re-fire the send.
  const initialQ = sp.get("q");
  const [qHandled, setQHandled] = useState(false);
  useEffect(() => {
    if (!initialQ || qHandled || !initialised) return;
    setInput(initialQ);
    setQHandled(true);
    // Small delay so the input renders + focus lands before we fire.
    setTimeout(() => {
      if (user) {
        send(initialQ);
      } else {
        inputRef.current?.focus();
      }
      // Clear the query param so refreshes don't re-send.
      const next = new URLSearchParams(sp);
      next.delete("q");
      navigate(`/wikifi${next.toString() ? "?" + next.toString() : ""}`, { replace: true });
    }, 120);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQ, qHandled, initialised, user]);

  // Keep global model + workspace mode in sync when the header picker changes.
  useEffect(() => {
    if (pickedModel?.wikifiId && pickedModel.wikifiId !== mode && mode !== "auto") {
      setMode(pickedModel.wikifiId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedModel?.wikifiId]);

  // When the user changes model inside the workspace, propagate up so header
  // reflects the same choice on other pages.
  useEffect(() => {
    if (!mode || mode === "auto") return;
    const match = frontierModels.find(m => m.wikifiId === mode);
    if (match && match.id !== pickedModel.id) setGlobalModelId(match.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Load a conversation when the active id changes.
  useEffect(() => {
    if (!activeId || !user) { setActiveConv(null); setMessages([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const c = await getConversation(activeId);
        if (cancelled) return;
        setActiveConv(c);
        setMessages(c.messages || []);
      } catch (e) {
        if (e?.response?.status === 404) {
          toast.error("Conversation not found");
          setActiveId(null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [activeId, user]);

  // Auto-scroll the thread on new messages / busy state.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const groups = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const filtered = q ? conversations.filter((c) => (c.title || "").toLowerCase().includes(q)) : conversations;
    return groupByDate(filtered);
  }, [conversations, filter]);

  const startNew = async () => {
    if (!user) { navigate("/login"); return; }
    try {
      const c = await createConversation({});
      setConversations((cs) => [{
        conversation_id: c.conversation_id,
        title: c.title,
        project: c.project,
        message_count: 0,
        updated_at: c.updated_at,
      }, ...cs]);
      setActiveId(c.conversation_id);
      setActiveConv(c);
      setMessages([]);
      navigate(`/wikifi?c=${c.conversation_id}`, { replace: true });
      setTimeout(() => inputRef.current?.focus(), 60);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start a new conversation.");
    }
  };

  const removeConv = async (cid) => {
    if (!window.confirm("Delete this conversation?")) return;
    try {
      await deleteConversation(cid);
      setConversations((cs) => cs.filter((c) => c.conversation_id !== cid));
      if (cid === activeId) { setActiveId(null); navigate("/wikifi", { replace: true }); }
    } catch (e) {
      toast.error("Couldn't delete conversation.");
    }
  };

  const send = async (rawText) => {
    const q = (rawText ?? input).trim();
    if (!q || busy) return;
    if (!user) { navigate("/login"); return; }

    // Ensure a conversation exists — create lazily on first send.
    let cid = activeId;
    let convForList = null;
    if (!cid) {
      try {
        const c = await createConversation({});
        cid = c.conversation_id;
        convForList = c;
        setActiveId(cid);
        setActiveConv(c);
        navigate(`/wikifi?c=${cid}`, { replace: true });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't start a conversation.");
        return;
      }
    }

    setInput("");
    // Optimistic user message
    const optimistic = { id: `tmp_${Date.now()}`, role: "user", text: q, created_at: new Date().toISOString() };
    setMessages((m) => [...m, optimistic]);
    setBusy(true);

    // Persist user message server-side (fire-and-forget from the UI's POV —
    // if it fails we still run the job so the user gets an answer).
    appendUserMessage(cid, q).then((serverMsg) => {
      setMessages((m) => m.map((x) => (x.id === optimistic.id ? serverMsg : x)));
    }).catch(() => { /* keep optimistic */ });

    try {
      const res = await runJob("wikifi", {
        question: q,
        conversation_id: cid,
        mode,
      });
      const assistant = {
        id: `asst_${Date.now()}`,
        role: "assistant",
        text: res.answer,
        model_used: res.model_used,
        orchestration: res.orchestration,
        models_consulted: res.models_consulted,
        created_at: new Date().toISOString(),
      };
      setMessages((m) => [...m, assistant]);
      // Bump the conversation to the top of the list
      setConversations((cs) => {
        const now = new Date().toISOString();
        const existing = cs.find((c) => c.conversation_id === cid);
        const nextTitle = (existing?.title && existing.title !== "New conversation")
          ? existing.title
          : (q.slice(0, 60) || "New conversation");
        const nextItem = existing
          ? { ...existing, title: nextTitle, updated_at: now, message_count: (existing.message_count || 0) + 2 }
          : {
              conversation_id: cid,
              title: nextTitle,
              project: convForList?.project ?? null,
              message_count: 2,
              updated_at: now,
            };
        return [nextItem, ...cs.filter((c) => c.conversation_id !== cid)];
      });
    } catch (e) {
      const msg = e?.message || "Something went wrong. Please try again.";
      setMessages((m) => [...m, {
        id: `err_${Date.now()}`, role: "assistant", text: msg, error: true, created_at: new Date().toISOString(),
      }]);
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent?.isComposing) {
      e.preventDefault();
      send();
    }
  };

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-white flex" data-testid="wikifi-workspace">
      {/* Workspace column — full width; the site-wide black nav has been
          removed, everything sits below the sticky top header now. */}
      <div className="flex-1 min-w-0 flex flex-col">
      {/* Top strip — brand + dual-product link */}
      <header className="h-[52px] shrink-0 border-b border-[#E7EAF0] bg-white flex items-center px-4 sm:px-6 gap-4">
        <Link to="/wikifi" className="flex items-center gap-2 group" data-testid="wikifi-brand">
          <span
            className="font-display inline-flex items-center justify-center shrink-0 rounded-full text-white leading-none"
            style={{ width: 26, height: 26, background: "#0B1220", fontSize: 13, fontWeight: 700 }}
            aria-hidden="true"
          >
            W
          </span>
          <span className="font-display font-bold text-[19px] text-[#0B1220] leading-none tracking-tight" style={{ letterSpacing: "-0.03em" }}>
            Wikifi
          </span>
          </Link>
        <div className="h-4 w-px bg-[#E7EAF0]" aria-hidden="true"/>
        <Link
          to="/"
          className="hidden sm:inline-flex items-center gap-1.5 text-[12px] text-[#5B6478] hover:text-[#0B1220] transition-colors group"
          data-testid="link-wikificial"
        >
          <span className="font-semibold text-[#0B1220]">Wikificial</span>
          <span className="text-[#8A93A6]">— the AI directory</span>
        </Link>
        <div className="ml-auto flex items-center gap-3">
          <MyStackRail variant="header"/>
          <Link to="/" className="text-[11.5px] font-medium text-[#5B6478] hover:text-[#0B1220] transition-colors" data-testid="link-directory-cta">
            Explore Wikificial ↗
          </Link>
          {user ? (
            <Link to="/dashboard" className="w-8 h-8 rounded-full bg-[#0B1220] text-white flex items-center justify-center font-bold text-[12px]" data-testid="wikifi-avatar" title={user.email}>
              {(user.email?.[0] || "W").toUpperCase()}
            </Link>
          ) : (
            <Link to="/login" className="h-8 px-3 rounded-md bg-white text-[#0B1220] border border-[#E7EAF0] hover:border-[#0B1220]/40 text-[12px] font-semibold flex items-center" data-testid="wikifi-signin">
              Sign in
            </Link>
          )}
        </div>
      </header>

      {/* Body — main chat column (full width) + collapsible right drawer.
          The history drawer is 280px, opens/closes with the top-bar panel
          toggle; when closed, the main column reclaims the whole width so
          the answer surface breathes. Signed-out users default to closed. */}
      <div className="flex-1 flex min-h-0">

        {/* Main conversation column */}
        <main className="flex-1 min-w-0 flex flex-col bg-white">
          {/* Sub-header — [+ New] · [Tool rail] · [Model picker] · [Drawer toggle] */}
          <div className="h-[52px] shrink-0 border-b border-[#E7EAF0] px-4 sm:px-6 flex items-center gap-2.5">
            <button
              onClick={startNew}
              className="h-9 px-3 rounded-[10px] bg-[#0B1220] text-white text-[13px] font-medium flex items-center gap-1.5 hover:brightness-110 transition-all shrink-0"
              data-testid="topbar-new-conversation-btn"
              title="Start a new conversation"
            >
              <Plus size={14}/>
              <span className="hidden sm:inline">New</span>
            </button>
            <div className="h-5 w-px bg-[#E7EAF0] shrink-0" aria-hidden="true"/>
            <MyStackRail variant="topbar"/>
            <div className="h-5 w-px bg-[#E7EAF0] shrink-0" aria-hidden="true"/>
            <ModelPicker value={mode} onChange={setMode} models={models}/>

            {/* History drawer toggle — pushed to the right. Uses PanelRight
                icon when closed (implies opening rightward) and PanelRightClose
                when open. */}
            <button
              onClick={() => setHistoryOpen(o => !o)}
              className="ml-auto h-9 w-9 inline-flex items-center justify-center rounded-md border border-[#E5E7EB] hover:border-[#0B1220] text-[#6B7280] hover:text-[#0B1220] transition-colors"
              data-testid="history-drawer-toggle"
              aria-label={historyOpen ? "Hide history" : "Show history"}
              title={historyOpen ? "Hide history" : "Show history"}
            >
              {historyOpen ? <PanelRightClose size={16}/> : <PanelRight size={16}/>}
            </button>
          </div>

          {activeConv && messages.length > 0 && (
            <div className="px-6 pt-3 pb-1">
              <div className="text-[11px] font-medium text-[#9CA3AF] truncate" data-testid="active-title">
                {activeConv.title || "New conversation"}
              </div>
            </div>
          )}

          {/* Thread / empty state */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto" data-testid="thread-scroll">
            {messages.length === 0 ? (
              <div className="max-w-[760px] mx-auto px-6 pt-14 pb-12" data-testid="empty-state">
                <PageHero
                  testId="wikifi-hero"
                  variant="bare"
                  eyebrow="Wikifi Workspace"
                  title="What do you want to accomplish?"
                  sub="Ask a question, share a task, or pick a starting point below. Wikifi Auto routes it to the right AI for the job."
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {SUGGESTED_PROMPTS.map((s) => (
                    <button
                      key={s.label}
                      onClick={() => { setInput(s.prompt); inputRef.current?.focus(); }}
                      className="text-left px-3.5 py-3 rounded-lg bg-white border border-[#E7EAF0] hover:border-[#0B1220] hover:bg-[#FAFAFA] transition-colors group"
                      data-testid={`suggested-${s.label.toLowerCase().replace(/\W+/g, "-")}`}
                    >
                      <div className="flex items-center gap-2.5">
                        <s.icon size={14} className="text-[#9CA3AF] group-hover:text-[#0B1220] transition-colors"/>
                        <span className="text-[14px] font-medium text-[#0B1220]">{s.label}</span>
                      </div>
                    </button>
                  ))}
                </div>
                <div className="mt-8 text-[12px] text-[#9CA3AF] flex items-center gap-1.5">
                  <span className="w-1 h-1 rounded-full bg-[#F97316]" aria-hidden="true"/>
                  Wikifi Auto is selected. Change to a specific model any time.
                </div>
                <MyStackRail variant="body"/>
                <OrchestratorPanel/>
              </div>
            ) : (
              <div className="max-w-[760px] mx-auto px-6 py-8 space-y-6">
                {messages.map((m) => (
                  <MessageBubble key={m.id} message={m}/>
                ))}
                {busy && (
                  <div className="flex items-center gap-2 text-[13px] text-[#9CA3AF]" data-testid="thread-thinking">
                    <Loader2 size={13} className="animate-spin"/>
                    <span>Wikifi is thinking…</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Input bar */}
          <div className="shrink-0 border-t border-[#E7EAF0] bg-white px-6 py-4">
            <div className="max-w-[760px] mx-auto">
              {/* Signed-out inline notice — replaces the old dead-space panel.
                  Sits directly above the composer, one line, muted. */}
              {!user && (
                <div className="mb-2 text-[13px] text-[#6B7280]" data-testid="signin-inline-notice">
                  <Link to="/login" className="text-[#0B1220] underline underline-offset-2 font-medium">Sign in</Link>
                  {" "}to save your conversations across sessions.
                </div>
              )}
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder={user ? "What do you want to accomplish?" : "Sign in to start using Wikifi…"}
                  rows={1}
                  className="flex-1 resize-none text-[14px] px-3.5 py-2.5 rounded-[10px] border border-[#E5E7EB] bg-white focus:outline-none focus:border-[#0B1220] focus:shadow-[0_0_0_3px_rgba(11,18,32,0.08)] transition-all leading-relaxed max-h-40 placeholder:text-[#9CA3AF]"
                  data-testid="workspace-input"
                  disabled={busy || !user}
                />
                <button
                  onClick={() => send()}
                  disabled={busy || !input.trim() || !user}
                  className="h-11 w-11 rounded-[10px] bg-[#F97316] hover:bg-[#EA6A0A] text-white flex items-center justify-center disabled:bg-[#E5E5E5] disabled:text-[#9CA3AF] disabled:cursor-not-allowed shrink-0 transition-colors"
                  data-testid="workspace-send"
                  aria-label="Send"
                >
                  {busy ? <Loader2 size={15} className="animate-spin"/> : <Send size={15}/>}
                </button>
              </div>
              <div className="mt-2 flex items-center justify-between text-[11px] text-[#9CA3AF]">
                <div>Enter to send · Shift+Enter for a new line</div>
                <div className="hidden md:block">Not medical / legal / financial advice. Verify what matters.</div>
              </div>
            </div>
          </div>
        </main>

        {/* Right-side history drawer — 280px, collapsible. Sits AFTER main
            so main takes the full remaining width when collapsed. */}
        {historyOpen && (
          <aside
            className="hidden md:flex w-[280px] shrink-0 border-l border-[#E7EAF0] bg-[#FAFAFA] flex-col"
            data-testid="wikifi-sidebar"
          >
            <div className="p-3 border-b border-[#E7EAF0] bg-white flex items-center justify-between gap-2">
              <div className="wf-label">History</div>
              <button
                onClick={() => setHistoryOpen(false)}
                className="h-7 w-7 rounded-md hover:bg-[#F5F5F5] text-[#9CA3AF] hover:text-[#0B1220] inline-flex items-center justify-center transition-colors"
                aria-label="Close history"
                data-testid="history-drawer-close"
              >
                <PanelRightClose size={14}/>
              </button>
            </div>
            <div className="p-3 border-b border-[#E7EAF0] bg-white">
              <div className="relative">
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]"/>
                <input
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Search history"
                  className="w-full h-8 rounded-md bg-white border border-[#E5E7EB] pl-7 pr-2 text-[12px] placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#0B1220]"
                  data-testid="history-search"
                />
              </div>
              <button
                onClick={startNew}
                className="hidden"
                data-testid="new-conversation-btn"
                aria-hidden="true"
                tabIndex={-1}
              >New</button>
            </div>
            <div className="flex-1 overflow-y-auto px-2 py-2 space-y-3" data-testid="history-list">
              {!user && initialised && (
                <div className="text-[12px] text-[#6B7280] px-2 py-4 leading-relaxed">
                  <Link to="/login" className="text-[#0B1220] font-medium underline">Sign in</Link> to save conversations across sessions.
                </div>
              )}
              {user && conversations.length === 0 && initialised && (
                <div className="text-[12px] text-[#9CA3AF] px-2 py-4 leading-relaxed">
                  No conversations yet.
                </div>
              )}
              {[
                ["Today", groups.today],
                ["This week", groups.week],
                ["Earlier", [...groups.yesterday, ...groups.older]],
              ].map(([label, items]) => items.length > 0 && (
                <div key={label}>
                  <div className="px-2 pb-1 wf-label" style={{ fontSize: 11 }}>{label}</div>
                  <div className="space-y-0.5">
                    {items.map((c) => (
                      <HistoryItem
                        key={c.conversation_id}
                        conv={c}
                        active={c.conversation_id === activeId}
                        onOpen={() => { setActiveId(c.conversation_id); navigate(`/wikifi?c=${c.conversation_id}`, { replace: true }); }}
                        onDelete={() => removeConv(c.conversation_id)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </aside>
        )}
      </div>
      </div>
    </div>
  );
}

function MessageBubble({ message }) {
  const isUser = message.role === "user";
  const isErr = !!message.error;

  // User messages keep a subtle right-side pill (Grok-style visual distinction).
  // Assistant / error messages render directly on the page white — no bubble
  // background — with rich markdown typography so answers feel like real
  // documents, not chat bubbles.
  if (isUser) {
    return (
      <div className="flex justify-end" data-testid="msg-user">
        <div className="max-w-[80%] text-[15px] leading-relaxed whitespace-pre-wrap px-4 py-2.5 rounded-xl bg-[#F5F5F5] text-[#0B1220]">
          {message.text}
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-start" data-testid={isErr ? "msg-error" : "msg-assistant"}>
      <div className="w-full max-w-[860px]">
        {isErr ? (
          <div className="text-[13.5px] leading-relaxed text-rose-800 bg-rose-50/60 border border-rose-100 rounded-md px-3.5 py-2.5 whitespace-pre-wrap">
            {message.text}
          </div>
        ) : (
          <div className="wikifi-prose text-[#0B1220]">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ node, ...props }) => (
                  <a {...props} target="_blank" rel="noopener noreferrer" className="text-[#F97316] hover:text-[#EA6A0A] underline underline-offset-2"/>
                ),
              }}
            >
              {message.text || ""}
            </ReactMarkdown>
          </div>
        )}
        {!isErr && (message.model_used || message.orchestration) && (
          <PoweredByWikifi meta={{
            model_used: message.model_used,
            orchestration: message.orchestration,
            models_consulted: message.models_consulted,
          }}/>
        )}
      </div>
    </div>
  );
}
